"use client";

import React, { useState } from "react";
import { LGS } from "modules/lgs/model/lgs";
import DynamicSimpleMap from "modules/lgs/components/stores/map/DynamicSimpleMap";
import { Info, MapPin, User } from "lucide-react";
import Link from "next/link";
import { ICON_SIZE } from "modules/common/components/navigation/Menu";
import { ShareButton } from "modules/common/components/ShareButton";
import { Modal } from "modules/common/components/Modal/Modal";
import { ensureValidWebsiteUrl } from "utils/utils";
import {
  createCityUrl,
  nameToSlug,
} from "modules/common/components/map/utils/formatCityName";
import { MobileFilterBar } from "modules/common/components/MobileFilterBar";
import { clientLogger } from "modules/common/logger/client";

type Props = {
  lgs: LGS;
  cityName?: string | null;
};

export function LGSHeader({ lgs, cityName }: Props) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Generate city link with country/city hierarchical URL
  // Must be before early return (hooks requirement)
  const cityLink = React.useMemo(() => {
    if (!cityName) return null;

    // cityName format: "Venice, Italy"
    const parts = cityName.split(",").map((s) => s.trim());

    if (parts.length >= 2) {
      const city = parts[0];
      const country = parts[parts.length - 1];

      // Validate that city and country are not empty after trimming
      if (!city || !country) {
        clientLogger.warn("lgsHeader.cityUrl", "invalid city or country name", {
          cityName,
        });
        return null;
      }

      // Validate that slugs won't be empty after conversion
      // This prevents issues with non-Latin characters being stripped
      const citySlug = nameToSlug(city);
      const countrySlug = nameToSlug(country);

      if (!citySlug || !countrySlug) {
        clientLogger.warn(
          "lgsHeader.cityUrl",
          "unable to create valid URL slug (likely non-Latin characters)",
          { cityName, citySlug, countrySlug }
        );
        return null;
      }

      return createCityUrl(country, city);
    }

    return null;
  }, [cityName]);

  if (!lgs) return null;

  // Ensure valid URL with protocol
  const validWebsiteUrl = ensureValidWebsiteUrl(lgs.website);

  const storeDetailsContent = (
    <div className="flex flex-col gap-4">
      {/* Store information section */}
      {(lgs.showEmailInSEL || lgs.postalAddress || lgs.website) && (
        <div className="flex flex-col gap-3">
          {/* Contact information */}
          {lgs.showEmailInSEL && (
            <>
              <div className="flex flex-wrap items-center gap-2 md:flex-nowrap">
                <span className="text-base-content font-medium">Number:</span>
                <span className="text-secondary">{lgs.phoneNumber}</span>
              </div>
              <div className="flex flex-wrap items-center gap-2 md:flex-nowrap">
                <span className="text-base-content font-medium">Email:</span>
                <span className="text-secondary">{lgs.emailAddress}</span>
              </div>
            </>
          )}

          {/* Address */}
          {lgs.postalAddress && (
            <div className="flex flex-wrap items-center gap-2 md:flex-nowrap">
              <span className="text-base-content font-medium">Address:</span>
              <span className="text-secondary">{lgs.postalAddress}</span>
            </div>
          )}

          {/* Website */}
          {validWebsiteUrl && (
            <div className="flex flex-wrap items-center gap-2 md:flex-nowrap">
              <span className="text-base-content font-medium">Website:</span>
              <Link
                href={validWebsiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-secondary hover:underline"
              >
                {lgs.website}
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Map section */}
      <div className="h-64 w-full">
        {lgs.latitude && lgs.longitude && <DynamicSimpleMap store={lgs} />}
      </div>
    </div>
  );

  const handleModalClose = () => setIsModalOpen(false);

  const mobileActions = [
    {
      icon: <Info size={ICON_SIZE} />,
      label: "Store Info",
      onClick: () => setIsModalOpen(true),
    },
  ];

  return (
    <>
      {/* Mobile Store Info Chip */}
      <MobileFilterBar actions={mobileActions} />

      <Modal
        open={isModalOpen}
        onClose={handleModalClose}
        title={`${lgs.name} Details`}
        maxWidth="2xl"
        height="auto"
        actions={
          <>
            <button className="btn btn-ghost btn-sm" onClick={handleModalClose}>
              Close
            </button>

            <ShareButton
              url={`/stores/${lgs.slug}`}
              title={`Check out ${lgs.name} on Lootstack`}
              text={`${lgs.name} - Find Magic: The Gathering cards to trade`}
              buttonText="Share"
              variant={"ghost"}
              className="btn-outline flex-1 md:flex-none"
              size={"sm"}
              iconOnly
            />
            <Link
              href={`/stores/${lgs.slug}/traders`}
              className="btn btn-secondary btn-sm flex-1 md:flex-none"
            >
              <User size={ICON_SIZE} className="md:ml-1" />
              <span className="hidden md:inline">Traders</span>
              {lgs?.userCount || 0}
            </Link>
            {cityLink && (
              <Link
                href={`${cityLink}/haves`}
                className="btn btn-primary btn-sm flex-1 md:flex-none"
                title={`View all trades in ${cityName}`}
              >
                <MapPin size={ICON_SIZE} />
                City <span className="hidden md:inline"> Trading</span>
              </Link>
            )}
          </>
        }
      >
        {storeDetailsContent}
      </Modal>

      {/* Desktop: Slim header — name, actions, description */}
      <div className="hidden flex-col md:flex">
        <div className="flex flex-col gap-2">
          {/* Header section with title and action buttons */}
          <div className="flex items-center justify-between">
            <h1 className="text-base-content text-2xl font-bold">{lgs.name}</h1>
            <div className="flex gap-2">
              <ShareButton
                url={`/stores/${lgs.slug}`}
                title={`Check out ${lgs.name} on Lootstack`}
                text={`${lgs.name} - Find Magic: The Gathering cards to trade`}
                buttonText="Share"
              />
              <Link
                href={`/stores/${lgs.slug}/traders`}
                className="btn btn-ghost btn-sm items-center"
              >
                <User size={ICON_SIZE} className="ml-1" />
                Traders {lgs?.userCount || 0}
              </Link>
            </div>
          </div>
          <p className="text-base-content/70">
            Browse available cards and find trading opportunities at {lgs.name}
          </p>
        </div>
      </div>
    </>
  );
}
